import type { RewarePublicTypes } from '@troverecommerce/shared-rest-api';
import { exec } from '../utils';

/**
 * Result of the email-first entry point — a discriminated union on `method`, so `idpId` exists only
 * on the SSO branch and callers must narrow before reading it.
 * - `MAGIC_LINK`: passwordless. If the address maps to a magic-link-enabled account a sign-in email
 *   was sent as a side effect; this is also returned for unknown emails, so the caller can't
 *   enumerate registered addresses. The client sends the user to the code page.
 * - `SSO`: the address is a registered account whose partner requires SSO. `idpId` is the Okta IdP
 *   to redirect to, or `null` (do a default Okta login).
 */
export type InitiateSignInResult =
  | { method: 'MAGIC_LINK' }
  | { method: 'SSO', idpId: string | null };

export type SignInMethod = InitiateSignInResult['method'];

/**
 * Single email-first router: given an email, reports how the account signs in (and, for magic-link,
 * triggers the send server-side). The client renders one email field and branches on `method`.
 */
export const initiateSignIn = async ({
  email,
}: RewarePublicTypes.PublicMutationsInitiateSignInArgs): Promise<InitiateSignInResult> => {
  const query = `
    mutation initiateSignIn($email: String!) {
      initiateSignIn(email: $email) {
        method
        sso {
          idpId
        }
      }
    }
  `;

  const data = await exec<
    RewarePublicTypes.PublicMutationsInitiateSignInArgs,
    Pick<RewarePublicTypes.PublicMutations, 'initiateSignIn'>
  >(query, { variables: { email } });

  const result = data.initiateSignIn;
  // The enum serializes to its string value; normalize by string compare (not the enum value, which
  // would be a runtime import). Default a missing/unknown method to passwordless — never a security
  // downgrade.
  if (String(result?.method) === 'SSO') {
    return { method: 'SSO', idpId: result?.sso?.idpId ?? null };
  }
  return { method: 'MAGIC_LINK' };
};
